/**
 * Converts encrypted mailbox records into authoritative spendable notes for
 * a Solana pool. Mailbox leaf, tree, token-domain and spent metadata are
 * never trusted: a note becomes spendable only after its commitment is
 * located in a tree rebuilt from finalized events, its token domain is
 * recomputed from the pool, its ownership is checked against the connected
 * key and its nullifier PDA is confirmed absent.
 */
import { solanaPoolAccountExists } from "./accounts";
import {
  computeSolanaTokenHash,
  decodeSolanaPoolConfig,
  getSolanaAccounts,
  solanaPoolAddress160,
  solanaPoolAddresses,
} from "@stealthos/sdk";
import { ethers } from "ethers";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../../joinsplit/types";
import { computeNullifier } from "../../joinsplit/keys";
import type { SolanaNetworkConfig } from "../../config/solana";
import { BN254_SCALAR_FIELD } from "../notes";
import { assertNotAborted, fail } from "./common";
import type { SolanaLeafCache } from "./leafCache";
import { scanSolanaTree, type SolanaTreeScan } from "./leafDiscovery";
import {
  assertSolanaFlatNote,
  isSolanaFlatNote,
  solanaNoteMint,
  solanaTreeKey,
} from "./notes";
import type { SolanaRpc } from "./rpc";

const ACCOUNT_BATCH = 100;

interface NoteCandidate {
  stored: StoredJoinSplitNote;
  note: JoinSplitNote;
  commitment: string;
  treeIndex?: number;
  precheckError?: string;
}

export interface SolanaNoteSyncResult {
  validated: Map<string, StoredJoinSplitNote>;
  spent: Set<string>;
  pending: Set<string>;
  rejected: Map<string, string>;
  /** Finalized slot of the pool config read; tree scans are at or after it. */
  slot: number;
  currentTreeIndex: number;
}

function canonicalCommitment(value: unknown): string {
  if (typeof value !== "string" || !ethers.isHexString(value, 32)) {
    throw new Error("note commitment must be exactly 32 bytes");
  }
  const commitment = value.toLowerCase();
  const scalar = BigInt(commitment);
  if (scalar === 0n || scalar >= BN254_SCALAR_FIELD) {
    throw new Error("note commitment is not a canonical nonzero field element");
  }
  return commitment;
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Read `PoolConfig` at finality and refuse a pool built for another cluster. */
export async function readSolanaPoolConfig(
  rpc: SolanaRpc,
  network: Pick<SolanaNetworkConfig, "programId" | "chainDomainId">,
) {
  const addresses = solanaPoolAddresses(network.programId);
  const { slot, accounts } = await getSolanaAccounts(
    rpc,
    [addresses.poolConfig],
    "finalized",
  );
  const info = accounts[0];
  if (!info) fail("pool is not initialized at this program id");
  if (info.owner !== addresses.programId) {
    fail("pool config account is not owned by the pool program");
  }
  const poolConfig = decodeSolanaPoolConfig(info.data);
  if (poolConfig.chainDomainId !== network.chainDomainId) {
    fail("pool is deployed for another cluster");
  }
  if (
    poolConfig.poolAddress160 !==
    solanaPoolAddress160(addresses.programId, addresses.poolConfig)
  ) {
    fail("on-chain pool projection differs from the SDK's");
  }
  return { slot, poolConfig, addresses };
}

export async function synchronizeSolanaNotes(params: {
  rpc: SolanaRpc;
  network: SolanaNetworkConfig;
  spendingKeys: SpendingKeys;
  notes: StoredJoinSplitNote[];
  cache?: SolanaLeafCache;
  signal?: AbortSignal;
  maxTreeScans?: number;
  requireDurableLeaves?: boolean;
}): Promise<SolanaNoteSyncResult> {
  const { rpc, network } = params;
  const maxTreeScans = params.maxTreeScans ?? network.maxTreeScans;
  if (!Number.isSafeInteger(maxTreeScans) || maxTreeScans < 1) {
    fail("maxTreeScans must be a positive integer");
  }
  const { slot, poolConfig, addresses } = await readSolanaPoolConfig(
    rpc,
    network,
  );
  const currentTreeIndex = poolConfig.currentTreeIndex;

  const validated = new Map<string, StoredJoinSplitNote>();
  const spent = new Set<string>();
  const pending = new Set<string>();
  const rejected = new Map<string, string>();
  const candidatesByCommitment = new Map<string, NoteCandidate[]>();

  params.notes.forEach((stored, index) => {
    const note = (stored as StoredJoinSplitNote | undefined)?.note;
    let commitment: string;
    try {
      commitment = canonicalCommitment(note?.commitment);
    } catch (error) {
      const key =
        typeof note?.commitment === "string"
          ? note.commitment.toLowerCase()
          : `invalid-record:${index}`;
      rejected.set(
        rejected.has(key) ? `${key}#record-${index}` : key,
        message(error),
      );
      return;
    }
    const precheckError = !isSolanaFlatNote(note, network)
      ? "note is outside this Solana pool's namespace"
      : note!.treeIndex === undefined ||
          !Number.isSafeInteger(note!.treeIndex) ||
          note!.treeIndex < 0
        ? "note has no canonical tree index"
        : undefined;
    const candidates = candidatesByCommitment.get(commitment) || [];
    candidates.push({
      stored,
      note: note!,
      commitment,
      treeIndex: precheckError ? undefined : note!.treeIndex,
      precheckError,
    });
    candidatesByCommitment.set(commitment, candidates);
  });

  const requestedTrees = new Set<number>();
  for (const candidates of candidatesByCommitment.values()) {
    for (const candidate of candidates) {
      if (
        candidate.treeIndex !== undefined &&
        candidate.treeIndex <= currentTreeIndex
      ) {
        requestedTrees.add(candidate.treeIndex);
      }
    }
  }
  const selectedTrees = [...requestedTrees]
    .sort((left, right) => {
      if (left === currentTreeIndex) return -1;
      if (right === currentTreeIndex) return 1;
      return right - left;
    })
    .slice(0, maxTreeScans);

  const scans = new Map<number, SolanaTreeScan>();
  for (const treeIndex of selectedTrees) {
    assertNotAborted(params.signal, "during note sync");
    scans.set(
      treeIndex,
      await scanSolanaTree({
        rpc,
        network,
        treeIndex,
        cache: params.cache,
        signal: params.signal,
        requireDurableLeaves: params.requireDurableLeaves,
      }),
    );
  }

  const tokenHashFor = (treeIndex: number, mint: string | null): string =>
    computeSolanaTokenHash({
      cluster: network.cluster,
      programId: addresses.programId,
      poolConfig: addresses.poolConfig,
      treeIndex,
      mint,
    });

  const authenticated: Array<{
    commitment: string;
    stored: StoredJoinSplitNote;
    nullifier: string;
  }> = [];

  for (const [commitment, candidates] of candidatesByCommitment) {
    let winner: StoredJoinSplitNote | undefined;
    let winnerNullifier = "";
    let sawPending = false;
    const errors: string[] = [];
    for (const candidate of candidates) {
      if (candidate.precheckError || candidate.treeIndex === undefined) {
        errors.push(
          candidate.precheckError || "note has no canonical tree index",
        );
        continue;
      }
      const scan = scans.get(candidate.treeIndex);
      if (!scan) {
        sawPending = true;
        continue;
      }
      const location = scan.tree.locate(commitment);
      if (!location) {
        sawPending = true;
        continue;
      }
      let mint: string | null;
      try {
        mint = solanaNoteMint(candidate.note);
      } catch (error) {
        errors.push(message(error));
        continue;
      }
      const authoritative: JoinSplitNote = {
        ...candidate.note,
        commitment,
        leafIndex: location.leafIndex,
        treeIndex: candidate.treeIndex,
        treeKey: solanaTreeKey(
          network.key,
          addresses.programId,
          candidate.treeIndex,
        ),
        merkleRoot: scan.root,
      };
      try {
        await assertSolanaFlatNote({
          note: authoritative,
          network,
          mint,
          expectedTokenHash: tokenHashFor(candidate.treeIndex, mint),
          expectedMpk: params.spendingKeys.mpk,
        });
      } catch (error) {
        errors.push(message(error));
        continue;
      }
      winnerNullifier = await computeNullifier(
        params.spendingKeys.nullifyingKey,
        authoritative.tokenHash,
        authoritative.leafIndex,
      );
      winner = { ...candidate.stored, note: authoritative, spent: false };
      break;
    }
    if (winner) {
      authenticated.push({
        commitment,
        stored: winner,
        nullifier: winnerNullifier,
      });
    } else if (sawPending) {
      pending.add(commitment);
    } else {
      rejected.set(
        commitment,
        [...new Set(errors)].join("; ") ||
          "no authenticated candidate matched this commitment",
      );
    }
  }

  for (let offset = 0; offset < authenticated.length; offset += ACCOUNT_BATCH) {
    assertNotAborted(params.signal, "during nullifier checks");
    const batch = authenticated.slice(offset, offset + ACCOUNT_BATCH);
    const { accounts } = await getSolanaAccounts(
      rpc,
      batch.map((entry) =>
        addresses.nullifier(entry.nullifier as `0x${string}`),
      ),
      "finalized",
    );
    batch.forEach((entry, index) => {
      if (
        solanaPoolAccountExists(accounts[index], network.programId, "nullifier")
      ) {
        spent.add(entry.commitment);
      } else {
        validated.set(entry.commitment, entry.stored);
      }
    });
  }

  return { validated, spent, pending, rejected, slot, currentTreeIndex };
}
